'use client';
import React, { ReactNode } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAppSelector } from '@/lib/hooks';
import { ActivityData, CourseData } from 'course-types';
import { EffectiveRole, role } from 'auth-types';
import EffectiveRoleService from '@/services/auth/effective-role-service';
import UtilityService from '@/services/util/utility-service';

interface BreadCrumb {
  displayName: string;
  pushPath: string;
}

interface BreadcrumbNavProps {
  pathName: string;
  course: CourseData;
  activity: ActivityData;
  darkMode: boolean;
  userRole: role;
  effectiveRole: EffectiveRole | null;
}

const getRoleDisplayName = (userRole: role): string => {
  if (userRole === 'enterprise_admin') {
    return 'Admin';
  }

  return UtilityService.cleanString(userRole.replace(/_/g, '-'));
};

export function BreadcrumbNav({
  pathName,
  course,
  activity,
  darkMode,
  userRole,
  effectiveRole,
}: BreadcrumbNavProps): ReactNode {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isEditMode = !!searchParams.get('edit');

  const activeView = useAppSelector((state) => state.isLoggedIn.activeView);

  if (userRole === '') return <></>;

  const normalizedPath = pathName.replace(/^\/+|\/+$/g, '');
  const slices = normalizedPath ? normalizedPath.split('/') : [];
  const resolvedRoleType = EffectiveRoleService.resolveRoleType(effectiveRole, userRole);
  const roleDisplayName =
    resolvedRoleType === 'student' || activeView === 'student'
      ? 'Student'
      : activeView === 'instructor'
        ? 'Instructor'
        : 'Admin';
  const rolePathRoot =
    resolvedRoleType === 'student' || activeView === 'student' ? 'student' : 'admin';
  const courseOverviewPath = resolvedRoleType === 'student' ? 'student/course' : 'admin/courses';
  const relativeSlices =
    slices.length > 0 && (slices[0] === 'student' || slices[0] === 'admin')
      ? slices.slice(1)
      : slices;

  if (relativeSlices.length === 1 && relativeSlices[0] === 'institute-overview') {
    return (
      <div className='flex flex-row'>
        <li
          onClick={() => router.push(`/${rolePathRoot}`)}
          className={`cursor-pointer ${darkMode ? 'text-primary_highlight' : 'text-primary'} hover:underline font-normal`}
        >
          {roleDisplayName}
        </li>
      </div>
    );
  }

  if (
    relativeSlices.length === 2 &&
    relativeSlices[0] === 'reports' &&
    relativeSlices[1] === 'overview'
  ) {
    return (
      <div className='flex flex-row gap-2'>
        <li
          onClick={() => router.push(`/${rolePathRoot}`)}
          className='cursor-pointer text-text_gray_600 hover:underline font-normal'
        >
          {roleDisplayName}
        </li>
        <p className='text-text_gray_600'>/</p>
        <li
          className={`cursor-pointer ${darkMode ? 'text-primary_highlight' : 'text-primary'} hover:underline font-normal`}
        >
          Reports
        </li>
      </div>
    );
  }

  const generatedCrumbs = relativeSlices.flatMap((slice, i) => {
    const crumbs: BreadCrumb[] = [];

    if (slice === 'lesson') {
      crumbs.push({ displayName: 'Course Overview', pushPath: courseOverviewPath });
      crumbs.push({
        displayName: course.name || 'Course',
        pushPath: [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/'),
      });
      return crumbs;
    }

    if (slice === 'edit') {
      crumbs.push({
        displayName: activity.name,
        pushPath:
          [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/') + `/?id=${activity.id}`,
      });
      return crumbs;
    }

    const editDefaultRoleParam = searchParams.get('role') ?? '';
    const editDefaultRoleTitle =
      editDefaultRoleParam === 'admin'
        ? 'Edit Admin Role'
        : editDefaultRoleParam === 'instructor'
          ? 'Edit Instructor Role'
          : editDefaultRoleParam === 'student'
            ? 'Edit Student Role'
            : 'Edit Role';

    const crumb: BreadCrumb = {
      displayName:
        slice === 'roles'
          ? 'Role Management'
          : slice === 'create-custom-role'
            ? isEditMode
              ? 'Edit Custom Role'
              : 'Create Custom Role'
            : slice === 'edit-default-role'
              ? editDefaultRoleTitle
              : UtilityService.cleanString(slice),
      pushPath: [rolePathRoot, ...relativeSlices.slice(0, i + 1)].join('/'),
    };

    return [crumb];
  });

  const breadCrumbs: BreadCrumb[] = [
    { displayName: roleDisplayName, pushPath: rolePathRoot },
    ...generatedCrumbs,
  ];

  return (
    <>
      {breadCrumbs.map((crumb, i) => (
        <div key={i} className='flex flex-row'>
          <li
            onClick={() => {
              router.push(`/${crumb.pushPath}`);
            }}
            className={`cursor-pointer ${i === breadCrumbs.length - 1 ? (darkMode ? 'text-primary_highlight' : 'text-primary') : 'text-text_gray_600'} hover:underline font-normal`}
          >
            {crumb.displayName}
          </li>

          <p className={`${i === breadCrumbs.length - 1 ? 'hidden' : 'mr-2'} text-text_gray_600`}>
            {' '}
            /{' '}
          </p>
        </div>
      ))}
    </>
  );
}
